// src/services/http.js
// Centralized HTTP client using axios with retry mechanism for cold starts
import axios from 'axios';

const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY = 500; // 500ms (fast retry for cold starts)

const api = axios.create({
  baseURL: `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api`,
  timeout: 15000, // 15 seconds timeout (increased for cold starts)
  headers: {
    'Content-Type': 'application/json',
    ...(process.env.REACT_APP_API_KEY && { 'X-API-Key': process.env.REACT_APP_API_KEY }),
  },
});

// Helper to check if error is retryable (timeout or network error)
const isRetryableError = (error) => {
  if (!error.config) return false;

  // Retry on timeout
  if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
    return true;
  }

  // Retry on network errors (no response received)
  if (!error.response) {
    return true;
  }

  // Retry on 500, 502, 503, 504 (common during cold starts)
  const retryableStatuses = [500, 502, 503, 504];
  if (error.response && retryableStatuses.includes(error.response.status)) {
    return true;
  }

  return false;
};

// Helper to delay execution
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Response interceptor for retry logic
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;

    // Initialize retry count
    config.__retryCount = config.__retryCount || 0;

    // Check if we should retry
    if (!isRetryableError(error) || config.__retryCount >= MAX_RETRIES) {
      return Promise.reject(error);
    }

    // Increment retry count
    config.__retryCount += 1;

    // Calculate delay with exponential backoff
    const retryDelay = INITIAL_RETRY_DELAY * Math.pow(2, config.__retryCount - 1);

    console.log(`Request to ${config.url} failed, retrying (${config.__retryCount}/${MAX_RETRIES}) in ${retryDelay}ms...`);

    // Wait before retrying
    await delay(retryDelay);

    // Retry the request
    return api(config);
  }
);

export default api;
